/**
 * ProShip Home Page — CT-style module directory.
 * All colors from foundation, styles from patterns, no hardcoded hex.
 */
import React, {useEffect, useRef, useState, useCallback} from 'react'
import {Box, Grid, Typography} from '@mui/material'
import {useNavigate} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {apiConstants, getGeneric} from '../../common/constants'
import {useGetCouriersQuery} from '../../redux/commonRTKPersist'
import {getMerchant} from '../../helpers/ReduxHelpers'
import PageTitle from '../../common/PageTitle'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {
  NAVY, BORDER, LIGHT_BG, SUBTLE, WHITE, PRIMARY_BLUE,
  SUCCESS, VERTICAL_COLORS, withOpacity,
} from '../../styles/foundation/colors'
import {SHADOW, HOVER} from '../../styles/foundation/effects'
import {pageContainerSx, sectionTitleSx} from '../../styles/patterns'

// ── Reusable styles (shared across this page) ──
const heroSx = {
  background: `linear-gradient(135deg, ${NAVY} 0%, #16213e 50%, #0f3460 100%)`,
  borderRadius: '12px', p: '28px', mb: '20px', position: 'relative', overflow: 'hidden',
  '&::after': {content: '""', position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: withOpacity(PRIMARY_BLUE, 0.15), pointerEvents: 'none'},
  '&::before': {content: '""', position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160, borderRadius: '50%', background: withOpacity(SUCCESS, 0.1), pointerEvents: 'none'},
}

const statCardSx = {
  flex: {xs: '1 1 130px', sm: '1 1 160px'}, bgcolor: WHITE, borderRadius: '10px', p: '14px 16px', textAlign: 'center',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover},
}

const statNumSx = {
  fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1.2,
  background: `linear-gradient(135deg, ${PRIMARY_BLUE}, ${SUCCESS})`,
  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
}

const featureCardSx = {
  bgcolor: WHITE, borderRadius: '10px', p: '12px 14px',
  boxShadow: '0 1px 6px rgba(0,0,0,0.04)', border: `1px solid ${BORDER}`,
  cursor: 'pointer', transition: 'all 0.2s', display: 'flex',
  alignItems: 'flex-start', gap: '10px', height: '100%',
  '&:hover': {transform: 'scale(1.01)', boxShadow: '0 4px 14px rgba(0,0,0,0.07)'},
}

const badgeSx = (color) => ({
  width: 22, height: 22, borderRadius: '50%', bgcolor: color, color: WHITE,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: WEIGHT.bold, flexShrink: 0, mt: 0.2,
})

// ── Animated counter (reusable hook) ──
function useCountUp(end, duration = 1500) {
  const [count, setCount] = useState(0)
  const ref = useRef(null)
  useEffect(() => {
    let start = null
    const step = (ts) => {
      if (!start) start = ts
      const p = Math.min((ts - start) / duration, 1)
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * end))
      if (p < 1) ref.current = requestAnimationFrame(step)
    }
    ref.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(ref.current)
  }, [end, duration])
  return count
}

const StatCard = React.memo(function StatCard({value, label, suffix = ''}) {
  const animated = useCountUp(value)
  return (
    <Box sx={statCardSx}>
      <Typography sx={statNumSx}>{animated.toLocaleString()}{suffix}</Typography>
      <Typography sx={{color: SUBTLE, fontSize: SIZE['2xs'], mt: '2px', fontWeight: WEIGHT.medium}}>{label}</Typography>
    </Box>
  )
})

// ── Data (all accent colors from VERTICAL_COLORS) ──
const STATS = [
  {value: 25, label: 'D2C Courier Partners', suffix: '+'},
  {value: 8, label: 'PTL Courier Partners', suffix: '+'},
  {value: 25900, label: 'PIN Codes Covered', suffix: '+'},
  {value: 15, label: 'Service Types', suffix: '+'},
  {value: 200, label: 'Courier Integrations', suffix: '+'},
]

const D2C_FEATURES = [
  {title: 'D2C Shipments', desc: 'Create, track & manage forward parcel shipments', icon: '📦', url: '/orders'},
  {title: 'Channel Orders', desc: 'Sync & fulfil Shopify, WooCommerce & other marketplace orders', icon: '🏪', url: '/channel-orders'},
  {title: 'Returns & RVP', desc: 'Reverse shipments & return requests', icon: '↩️', url: '/reverse-orders'},
  {title: 'NDR Management', desc: 'Non-delivery reports — reattempt, RTO, address fix', icon: '⚠️', url: '/ndr'},
  {title: 'NPR Management', desc: 'Non-pickup reports — reschedule failed pickups', icon: '🚌', url: '/npr'},
  {title: 'Weight Disputes', desc: 'Review & resolve weight discrepancy cases', icon: '⚖️', url: '/weight-discrepancy'},
  {title: 'Claims', desc: 'Lost, damaged & missing shipment claim tracking', icon: '📋', url: '/claims'},
  {title: 'D2C Rate Calculator', desc: 'Calculate D2C parcel shipping rates', icon: '🧮', url: '/rate-calculator'},
]

const PTL_FEATURES = [
  {title: 'PTL Shipments', desc: 'Part-truck-load B2B freight — create, track, manage', icon: '🚛', url: '/b2b-orders'},
  {title: 'Appointment Deliveries', desc: 'Scheduled B2B deliveries for retail & quick commerce', icon: '📅', url: '/appointments/all'},
  {title: 'PTL Rate Calculator', desc: 'Calculate B2B part-truck-load shipping rates', icon: '🧮', url: '/rate-calculator-ptl'},
]

const COMMON_FEATURES = [
  {title: 'Dashboard', desc: 'Shipment analytics — overview, courier, L&D', icon: '📊', url: '/dashboard'},
  {title: 'Analytics', desc: 'Performance analytics & deep-dive reports', icon: '📈', url: '/Performance'},
  {title: 'Control Tower', desc: 'Real-time delay monitoring — forward, pickup, RTO', icon: '🏢', url: '/control-tower'},
  {title: 'ProPilot AI', desc: 'AI-powered shipment insights & recommendations', icon: '🤖', url: '/proship-pilot'},
  {title: 'Wallet & Billing', desc: 'Wallet balance, COD remittance & invoices', icon: '💰', url: '/wallet'},
  {title: 'Serviceability', desc: 'PIN code coverage across all courier partners', icon: '📍', url: '/serviceable-pincodes'},
  {title: 'Reports & Exports', desc: 'MIS, NDR, COD & custom report generation', icon: '📄', url: '/reports'},
  {title: 'Shipping Rules', desc: 'Courier allocation rules for smart routing', icon: '⚙️', url: '/merchant-rules'},
  {title: 'Settings', desc: 'Locations, webhooks, notifications & config', icon: '🔧', url: '/setting'},
  {title: 'Profile & KYC', desc: 'Merchant profile, bank details & KYC status', icon: '👤', url: '/profile'},
]

// ── Feature grid section (reusable) ──
const FeatureGrid = React.memo(function FeatureGrid({title, subtitle, features, accent, startNum}) {
  const navigate = useNavigate()
  const handleClick = useCallback((url) => navigate(url), [navigate])

  return (
    <Box sx={{mb: 3}}>
      <Typography sx={{...sectionTitleSx, fontSize: SIZE.lg}}>{title}</Typography>
      <Typography sx={{color: SUBTLE, fontSize: SIZE['2xs'], mb: 2, fontFamily: FONT}}>{subtitle}</Typography>
      <Grid container spacing={1.5}>
        {features.map((f, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={f.url}>
            <Box sx={featureCardSx} onClick={() => handleClick(f.url)}>
              <Box sx={{width: 3, borderRadius: 3, alignSelf: 'stretch', flexShrink: 0, bgcolor: accent}} />
              <Box sx={badgeSx(accent)}>{startNum + i}</Box>
              <Box sx={{fontSize: 20, flexShrink: 0, mt: 0.1}}>{f.icon}</Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY, mb: '2px', fontFamily: FONT}}>{f.title}</Typography>
                <Typography sx={{fontSize: '10.5px', color: SUBTLE, lineHeight: 1.35, fontFamily: FONT}}>{f.desc}</Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
})

// ── Main component ──
export default function HomePage() {
  const {userInfo} = useSelector((state) => state.user)
  const {orderConversionDetails} = useSelector((state) => state.report)
  const firstName = userInfo?.username?.split('@')[0] || userInfo?.merchant?.name || 'there'

  // Channel Orders / Returns cards follow the same merchant-level visibility flags as the
  // sidebar (default visible; hidden only when an admin explicitly toggled the flag off).
  const d2cFeatures = D2C_FEATURES.filter((f) => {
    if (f.url === '/channel-orders') return orderConversionDetails?.channelOrdersVisibleMerchantPanel !== false
    if (f.url === '/reverse-orders') return orderConversionDetails?.returnsVisibleMerchantPanel !== false
    return true
  })

  // Serviceable PIN codes — from serviceability intel trends overview.
  // Falls back to the static figure if the call fails (e.g. non-admin role → 403).
  const [pinCovered, setPinCovered] = useState(null)
  useEffect(() => {
    let active = true
    getGeneric(apiConstants.SERVICEABILITY_TRENDS_OVERVIEW)
      .then((res) => {
        const v = res?.data?.responseObj?.kpis?.servicableToday?.value ?? res?.data?.responseObj?.universePinCount
        if (active && v != null) setPinCovered(Number(v))
      })
      .catch(() => {})
    return () => { active = false }
  }, [])

  // Courier Partners — distinct parent couriers, split by vertical. supported_shipment
  // 'B2C' = D2C and 'B2B' = PTL, the same discriminator the rest of the app uses.
  const {data: couriers = []} = useGetCouriersQuery()
  const countPartners = (shipmentType) =>
    new Set(couriers.filter((c) => c.supported_shipment === shipmentType).map((c) => c.parent).filter(Boolean)).size
  const d2cCourierCount = countPartners('B2C')
  const ptlCourierCount = countPartners('B2B')
  // Platform capability stats, also from the (ungated, platform-wide) courier master:
  // distinct service types offered, and total individual courier integrations.
  const serviceTypeCount = new Set(couriers.map((c) => c.serviceTypePrimary).filter(Boolean)).size
  const courierIntegrationCount = couriers.length

  // Merchant-level D2C/PTL toggles (admin panel > merchant details) — hide only when explicitly
  // disabled, same conservative convention used in Sidebar.js.
  const merchant = getMerchant()
  const showPtl = merchant?.isPTLMerchant !== false
  const showD2c = merchant?.isD2CMerchant !== false

  // Show a courier card only for the verticals this merchant uses, and fill in the live
  // per-vertical counts (falling back to the static figure until the list loads).
  const stats = STATS
    .filter((s) => {
      if (s.label === 'D2C Courier Partners') return showD2c
      if (s.label === 'PTL Courier Partners') return showPtl
      return true
    })
    .map((s) => {
      if (s.label === 'PIN Codes Covered' && pinCovered != null) return {...s, value: pinCovered}
      if (s.label === 'D2C Courier Partners' && d2cCourierCount > 0) return {...s, value: d2cCourierCount}
      if (s.label === 'PTL Courier Partners' && ptlCourierCount > 0) return {...s, value: ptlCourierCount}
      if (s.label === 'Service Types' && serviceTypeCount > 0) return {...s, value: serviceTypeCount}
      if (s.label === 'Courier Integrations' && courierIntegrationCount > 0) return {...s, value: courierIntegrationCount}
      return s
    })

  return (
    <Box sx={{...pageContainerSx, background: LIGHT_BG}}>
      <PageTitle>ProShip Merchant Panel</PageTitle>

      {/* Hero */}
      <Box sx={heroSx}>
        <Typography sx={{color: WHITE, fontWeight: WEIGHT.bold, fontSize: '20px', mb: '4px', fontFamily: FONT}}>
          Welcome back, {firstName}
        </Typography>
        <Typography sx={{color: withOpacity(WHITE, 0.7), fontSize: SIZE['2xs'], fontFamily: FONT}}>
          ProShip Merchant Panel — manage D2C shipments, PTL freight, returns, billing & more from one place.
        </Typography>
      </Box>

      {/* Stats */}
      <Box sx={{display: 'flex', gap: {xs: '12px', sm: '20px'}, mb: '36px', flexWrap: 'wrap'}}>
        {stats.map((s) => <StatCard key={s.label} {...s} />)}
      </Box>

      {/* Module grids */}
      {showD2c && (
        <FeatureGrid title='D2C Freight' subtitle='Direct-to-consumer parcel shipments — forward, reverse & marketplace fulfilment' features={d2cFeatures} accent={VERTICAL_COLORS.D2C.text} startNum={1} />
      )}
      {showPtl && (
        <FeatureGrid title='PTL Freight' subtitle='Part-truck-load B2B shipments — appointment & non-appointment deliveries' features={PTL_FEATURES} accent={VERTICAL_COLORS.PTL.text} startNum={9} />
      )}
      <FeatureGrid title='Account & Common' subtitle='Analytics, billing, serviceability, reports, settings & support' features={COMMON_FEATURES} accent={VERTICAL_COLORS.BOTH.text} startNum={12} />
    </Box>
  )
}
